"use client";

// ── the versions dropdown — every state a skill has been ────────────────────
// A skill's link never changes; the document behind it moves. Every state
// worth keeping is still reachable: the current one at the plain link, each
// superseded one at `?v=<id>`. Opening a past state changes the link (and so
// the code) to that state, so what you hand on is what you read
// (law_share_scope: a link opens exactly one thing).
//
// It reads as ONE quiet trigger naming the state you are in — `version 1.2 ⌄`,
// or the date when a state carries no version string — opening THE house
// frosted menu (the same `OptionsMenu` behind `kept by` on /community and
// `options ⌄` on every page): the state you are reading marked with the quiet
// ink-dot, each row carrying its date as the hint, a bottom sheet on phones.
// It used to be a strip of every state laid out as words, which grew with the
// skill and read as a row of unlabelled numbers.

import { useRef, useState } from "react";
import type { SkillVersion } from "../../lib/skills/types";
import OptionsMenu from "../os/artifact/OptionsMenu";
import type { MenuItem } from "../os/ContextMenu";
import "./skills.css";
// the trigger's own rules live with the share surfaces' sheet, which both call
// sites (the skill page and a shared skill) already carry.
import "../share/share.css";

export interface VersionsRailProps {
  versions: SkillVersion[];
  /** the id being read; null = the current state */
  viewing: string | null;
  /** the page's own path, without a query — `/skills/quiet-interface`, `/share/abc` */
  basePath: string;
}

/** the same chevron every dropdown trigger in the house wears. */
function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 13 13" fill="none" aria-hidden>
      <path d="M3.25 5.1 6.5 8.35 9.75 5.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const day = (v: SkillVersion): string | undefined => v.updated?.slice(0, 10);

function label(v: SkillVersion): string {
  if (v.version) return `version ${v.version}`;
  if (v.updated) return day(v) as string;
  return "an earlier state";
}

export function versionHref(basePath: string, v: SkillVersion): string {
  return v.current ? basePath : `${basePath}?v=${encodeURIComponent(v.id)}`;
}

export default function VersionsRail({ versions, viewing, basePath }: VersionsRailProps) {
  const trigger = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number; bottom: number } | null>(null);

  if (versions.length < 2) return null;
  const current = versions.find((v) => v.current) ?? null;
  const reading = viewing ? versions.find((v) => v.id === viewing) ?? null : null;
  const here = reading ?? current ?? versions[0];

  const items: MenuItem[] = versions.map((v) => {
    const on = viewing ? v.id === viewing : v.current;
    return {
      label: v.current ? `${label(v)} · current` : label(v),
      hint: day(v),
      selected: on,
      // the mark of the state you are reading — passed as the icon too, so the
      // phone's sheet (which knows no `selected`) shows the same quiet dot.
      icon: on ? <span className="ink-dot" aria-hidden /> : undefined,
      // exactly what the words did: the link changes, the whole page follows.
      onSelect: () => {
        if (!on) location.assign(versionHref(basePath, v));
      },
    };
  });

  return (
    <div className="sk-versions os-enter" style={{ ["--i" as string]: 5 }}>
      <button
        ref={trigger}
        type="button"
        className="os-link sk-versions__trigger"
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        onClick={() => {
          const r = trigger.current?.getBoundingClientRect();
          setAnchor((a) => (a || !r ? null : { x: r.left, y: r.top, bottom: r.bottom }));
        }}
      >
        <span className="os-link-text">
          {here.version ? <>version <b>{here.version}</b></> : <b>{label(here)}</b>}
        </span>
        <Chevron />
      </button>
      <OptionsMenu open={!!anchor} onClose={() => setAnchor(null)} items={items} anchor={anchor} title="versions" />
      {reading && !reading.current && (
        <p className="sk-versions__note">
          you&rsquo;re reading the state from {reading.updated ? reading.updated.slice(0, 10) : "earlier"}
          {reading.version ? ` (v${reading.version})` : ""}. the link and the code on this page point at exactly this state —{" "}
          <a className="quiet-link" href={basePath}>read the current one</a>
          {current?.version ? ` (v${current.version})` : ""}.
        </p>
      )}
    </div>
  );
}
